import { PlusOutlined, RightOutlined } from "@ant-design/icons";
import { isDefined } from "@clipboard-health/util-ts";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Alert, Box, IconButton, Stack, Tooltip } from "@mui/material";
import { type Qualification } from "@src/appV2/Admin/Qualifications/types";
import { showErrorToast, showSuccessToast, showWarningToast } from "@src/appV2/lib/Notifications";
import { Text } from "@src/lib/ui-react";
import { Button, Table } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AddHolidayFeeForm } from "./AddHolidayFeeForm";
import { useGetFacilityHolidayFeeOverrides } from "./api/useGetFacilityHolidayFeeOverrides";
import { useUpdateWorkplaceHolidayFeeOverrides } from "./api/useUpdateWorkplaceHolidayFeeOverrides";
import {
  addHolidayFeeOverride,
  flattenHolidayFeeOverrides,
  hasHolidayFeeOverride,
  removeHolidayFeeOverride,
} from "./qualificationHolidayFeeOverridesUtils";
import { getColumns } from "./tableColumns";
import {
  type HolidayIncrement,
  type QualificationHolidayFeeOverrideRow,
  type QualificationHolidayFeeOverrides as QualificationHolidayFeeOverridesType,
} from "./types";

export interface QualificationHolidayFeeOverridesEditorProps {
  hcfId: string;
  qualifications: Qualification[];
  workplaceId: string;
}

export function QualificationHolidayFeeOverridesEditor(
  props: QualificationHolidayFeeOverridesEditorProps
): JSX.Element {
  const { hcfId, qualifications, workplaceId } = props;
  const [extend, setExtend] = useState<boolean>(false);
  const [overrides, setOverrides] = useState<QualificationHolidayFeeOverridesType>({});
  const [showAddForm, setShowAddForm] = useState(false);

  const [newQualifications, setNewQualifications] = useState<string[]>([]);
  const [newType, setNewType] = useState<HolidayIncrement | undefined>();
  const [newIncrement, setNewIncrement] = useState<number | undefined>();

  const {
    overrides: fetchedOverrides,
    isLoading: isFetching,
    error,
    refetch,
  } = useGetFacilityHolidayFeeOverrides(hcfId);

  const { mutateAsync: updateWorkplace, isLoading: isUpdating } =
    useUpdateWorkplaceHolidayFeeOverrides();

  const flatOverrides = useMemo(() => flattenHolidayFeeOverrides(overrides), [overrides]);

  // Sync fetched overrides to local state, but skip if a save operation is in progress
  // to prevent race conditions where a background refetch could overwrite pending changes
  useEffect(() => {
    if (!isUpdating) {
      setOverrides(fetchedOverrides);
    }
  }, [fetchedOverrides, isUpdating]);

  // Reset form state when switching HCFs
  useEffect(() => {
    setShowAddForm(false);
    setNewQualifications([]);
    setNewType(undefined);
    setNewIncrement(undefined);
  }, [hcfId]);

  const saveOverrides = useCallback(
    async (newOverridesMap: QualificationHolidayFeeOverridesType): Promise<boolean> => {
      try {
        await updateWorkplace({
          workplaceId,
          qualificationHolidayFeeOverrides: newOverridesMap,
        });
        // Refetch to get the updated data from the server
        await refetch();
        showSuccessToast("Qualification holiday fee override saved successfully.");
        return true;
      } catch {
        // Error toast is shown by the mutation's onError handler
        return false;
      }
    },
    [workplaceId, updateWorkplace, refetch]
  );

  const handleDelete = useCallback(
    async (record: QualificationHolidayFeeOverrideRow) => {
      const updated = removeHolidayFeeOverride(overrides, record.qualification);
      await saveOverrides(updated);
    },
    [overrides, saveOverrides]
  );

  const handleAdd = useCallback(async () => {
    if (newQualifications.length === 0 || !newType || typeof newIncrement !== "number") {
      showErrorToast("Please fill in all fields.");
      return;
    }
    const existingQualifications = newQualifications.filter((qualification) =>
      hasHolidayFeeOverride(overrides, qualification)
    );
    if (existingQualifications.length > 0) {
      showWarningToast(`Overriding existing holiday fee for: ${existingQualifications.join(", ")}`);
    }
    let updated = { ...overrides };
    for (const qualification of newQualifications) {
      updated = addHolidayFeeOverride(updated, {
        qualification,
        type: newType,
        increment: newIncrement,
      });
    }
    const success = await saveOverrides(updated);
    if (success) {
      setNewQualifications([]);
      setNewType(undefined);
      setNewIncrement(undefined);
      setShowAddForm(false);
    }
  }, [newQualifications, newType, newIncrement, overrides, saveOverrides]);

  const qualificationOptions = qualifications.map((qualification) => ({
    value: qualification.attributes.name,
    label: qualification.attributes.displayLabel,
  }));

  const columns = getColumns({
    qualifications,
    isUpdating,
    onDelete: (record) => {
      handleDelete(record).catch(() => {
        // Error already handled in saveOverrides
      });
    },
  });

  const handleClearForm = () => {
    setShowAddForm(false);
    setNewQualifications([]);
    setNewType(undefined);
    setNewIncrement(undefined);
  };

  return (
    <Box sx={{ marginTop: 3 }}>
      <Text bold variant="subtitle2">
        <Stack alignItems="center" direction="row" spacing={0.5}>
          <span>Custom Holiday Rates per Qualification</span>
          <Tooltip title="Override holiday fee charges for specific qualifications.">
            <HelpOutlineIcon fontSize="small" />
          </Tooltip>
          <IconButton
            size="small"
            sx={(theme) => ({
              "&:hover": { backgroundColor: theme.palette.grey[200] },
              "&:active": { backgroundColor: theme.palette.grey[300] },
            })}
            onClick={() => {
              setExtend(!extend);
            }}
          >
            <RightOutlined
              style={{
                transform: extend ? "rotate(90deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          </IconButton>
        </Stack>
      </Text>
      <Box sx={{ display: extend ? "block" : "none", marginLeft: 2.5 }}>
        {isDefined(error) && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            Unable to fetch qualification holiday fee overrides
          </Alert>
        )}
        <Text sx={{ marginBottom: 2 }} variant="body2">
          Add custom holiday fee charges for specific qualifications.
        </Text>
        <Table
          columns={columns}
          dataSource={flatOverrides}
          loading={isFetching}
          locale={{ emptyText: "No custom holiday rates configured" }}
          pagination={false}
          rowKey={(record) => record.qualification}
          size="small"
        />
        {!showAddForm && (
          <Box sx={{ marginTop: 2 }}>
            <Button
              disabled={isUpdating}
              icon={<PlusOutlined />}
              type="dashed"
              onClick={() => {
                setShowAddForm(true);
              }}
            >
              Add Custom Holiday Rate
            </Button>
          </Box>
        )}
        {showAddForm && (
          <AddHolidayFeeForm
            isUpdating={isUpdating}
            newIncrement={newIncrement}
            newQualifications={newQualifications}
            newType={newType}
            qualificationOptions={qualificationOptions}
            onCancel={handleClearForm}
            onIncrementChange={setNewIncrement}
            onQualificationsChange={setNewQualifications}
            onSave={handleAdd}
            onTypeChange={setNewType}
          />
        )}
      </Box>
    </Box>
  );
}
